/* ============================================
   THE ADMIN SAMPLE PICKER
   ============================================
   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — *"a button which allows deal owners (admin only
   right now) to adjust the samples we're requesting… when clicked opens a menu with all sample
   options… they can select and deselect and search for samples here. when they add samples, it
   adds them to the list back in the DD under 'SAMPLES' header, then when they request, it updates
   the email accordingly."*

   Opened by `.rqd-sample-adjust`, which sits to the left of Request in the samples block
   (`dashboard-lead-detail.js`). Admin only, on both sides — the button is not rendered for a rep
   and the worker refuses the write regardless.

   ⛔ THE PICKER NEVER COMPOSES A NAME. The size suffix, the extras rule and the two fields a deal
   stores its list in all live in the worker (`lib/sample-compose.js`), because `_samplesOf` and
   the DD card read those fields independently and a second composer here would be a second thing
   to keep in step. This file sends a SELECTION — `{handle, name, swatch, size, kind}` — and
   renders back whatever the worker says the list became.

   ⚠️ SO THE CARD REPAINTS FROM THE SAVE RESPONSE, not from a re-fetch and not from local state.
   Re-fetching could show a different list from the one just written; painting from local state
   could show one the worker never accepted.
   ============================================ */
(function () {
  'use strict';

  var CATALOG = null;          // { products, extras, truncated, fetchedAt }
  var loadingCatalog = null;   // in-flight promise, so a double-click does not fetch twice

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function api() {
    return (window.RepPortal && window.RepPortal.API) || null;
  }

  /* One key per selectable thing. A product+colour is one row; an extra is one row.
     ⚠️ Keyed on handle+colour rather than on the label, so a product retitled in Shopify keeps
     its selection instead of silently dropping off the deal's list. */
  function keyOf(sel) {
    return sel.kind === 'extra'
      ? 'x:' + String(sel.name || '').toLowerCase()
      : 'p:' + String(sel.id || sel.handle || '') + '|' + String(sel.swatch || '');
  }

  function loadCatalog(force) {
    if (CATALOG && !force) return Promise.resolve(CATALOG);
    if (loadingCatalog) return loadingCatalog;
    loadingCatalog = api().get('/admin/sample-catalog' + (force ? '?refresh=1' : ''))
      .then(function (res) {
        if (!res || res.error) throw new Error((res && res.error) || 'catalog unavailable');
        CATALOG = res;
        return res;
      })
      .finally(function () { loadingCatalog = null; });
    return loadingCatalog;
  }

  /* ============================================
     open — the modal
     ============================================ */
  function open(ref, sourceBtn) {
    var A = api();
    if (!A) return;
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: opened from the block's own
       Adjust (`data-lane="2"`), the picker reads and writes THAT list (`recipient: 2` on both calls)
       and repaints that block only. */
    var lane2 = !!(sourceBtn && sourceBtn.dataset && sourceBtn.dataset.lane === '2');

    var prior = document.getElementById('rqd-sample-picker-overlay');
    if (prior) prior.remove();

    var overlay = document.createElement('div');
    overlay.id = 'rqd-sample-picker-overlay';
    overlay.className = 'rqd-daterange-backdrop';
    overlay.innerHTML = [
      '<div class="rqd-daterange-modal rqd-sample-picker" style="max-width:720px;padding:22px 26px;max-height:88vh;display:flex;flex-direction:column;">',
      '  <h3 class="rqd-daterange-modal__title" style="margin-top:0;">Samples for ' + esc(ref) + (lane2 ? ' (second recipient)' : '') + '</h3>',
      '  <div style="font-size:12px;color:var(--rq-text-secondary);margin-bottom:12px;">',
      (lane2 ? '    What we ask ECORE to ship to the second recipient. This replaces their list; the buyer’s own list is untouched.'
             : '    What we ask ECORE to ship. This replaces the list on the deal — the customer’s original selection is kept in the deal feed.'),
      '  </div>',
      '  <input type="text" id="rqd-sp-search" placeholder="Search samples…" autocomplete="off"',
      '         style="width:100%;padding:7px 10px;border:1px solid var(--rq-border);border-radius:6px;font-family:inherit;font-size:13px;background:var(--rq-bg-input);color:var(--rq-text);margin-bottom:10px;">',
      /* Ryan 2026-09-24: FLIPPED - the catalog (what you can pick from) is on TOP, under the
         search box it answers to; the chosen tray sits BELOW it and grows as it fills (CSS). */
      '  <div id="rqd-sp-list" class="rqd-sp-list">Loading…</div>',
      '  <div id="rqd-sp-selected" class="rqd-sp-selected"></div>',
      '  <div id="rqd-sp-note" style="font-size:11px;color:var(--rq-text-secondary);margin-top:8px;"></div>',
      '  <div class="rqd-daterange-modal__footer" style="margin-top:14px;display:flex;align-items:center;gap:8px;">',
      '    <button type="button" id="rqd-sp-cancel" class="rqd-pill" style="margin-right:auto;">Cancel</button>',
      '    <span id="rqd-sp-status" style="font-size:11px;color:var(--rq-text-secondary);"></span>',
      /* ⚠️ `rqd-pill--active`, not an invented `--primary`. The variants that exist are
         active/dead/new/prospect/sample; a class nobody defines renders as a bare pill, which
         would leave the confirm button looking identical to Cancel. */
      '    <button type="button" id="rqd-sp-save" class="rqd-pill rqd-pill--active">Save list</button>',
      '  </div>',
      '</div>',
    ].join('');

    (document.querySelector('.rep-quote-portal-wrap') || document.body).appendChild(overlay);

    var listEl = overlay.querySelector('#rqd-sp-list');
    var selEl = overlay.querySelector('#rqd-sp-selected');
    var searchEl = overlay.querySelector('#rqd-sp-search');
    var statusEl = overlay.querySelector('#rqd-sp-status');
    var noteEl = overlay.querySelector('#rqd-sp-note');
    var saveBtn = overlay.querySelector('#rqd-sp-save');

    /* The working set: key -> selection object. A Map so order is the order it was built in,
       which is what the worker preserves and what the desk reads. */
    var chosen = new Map();

    function close() { overlay.remove(); }
    overlay.querySelector('#rqd-sp-cancel').addEventListener('click', close);
    overlay.addEventListener('mousedown', function (e) { if (e.target === overlay) close(); });
    /* ⚠️ Escape closes, and a picker with unsaved edits says so first — the alternative is
       silently discarding a list somebody just spent a minute building. */
    document.addEventListener('keydown', function onKey(e) {
      if (!document.body.contains(overlay)) { document.removeEventListener('keydown', onKey); return; }
      if (e.key !== 'Escape') return;
      if (dirty && !confirm('Discard the changes to this sample list?')) return;
      document.removeEventListener('keydown', onKey);
      close();
    });

    var dirty = false;
    function touch() { dirty = true; renderSelected(); }

    /* ---- the chosen list, shown UNDER the catalog (Ryan 2026-09-24; it was above until then).
       It is a sibling of the catalog in the modal's column, not inside it, so it never scrolls
       out of sight either way. ---- */
    function renderSelected() {
      if (!chosen.size) {
        selEl.innerHTML = '<div class="rqd-sp-empty">Nothing selected yet.</div>';
        return;
      }
      var rows = [];
      chosen.forEach(function (s, k) {
        /* ⛔ THE SIZES ARE THE SAMPLE'S OWN, and a sample with only one gets NO control at all.
           This was a hardcoded 6"/12" until Ryan walked ECORE's supplier portal: FierceTurf is cut
           only at 9", the pavers ship whole, PlayGuard is 4" or 12", Heritage is 6" or a 6"x12"
           rectangle. Offering a choice that does not exist is how a work order ECORE cannot fill
           gets written. ⚠️ One size is shown as static text rather than a single dead button — a
           lone toggle invites a click that does nothing. */
        var opts = Array.isArray(s.sizes) ? s.sizes : [];
        var sizeCtl = '';
        if (s.kind !== 'extra' && opts.length > 1) {
          sizeCtl = '<span class="rqd-sp-size" role="group" aria-label="Sample size">'
            + opts.map(function (sz) {
              return '<button type="button" class="rqd-sp-size__opt' + (s.size === sz ? ' is-on' : '') + '"'
                + ' data-key="' + esc(k) + '" data-size="' + esc(sz) + '">' + esc(sz) + '</button>';
            }).join('')
            + '</span>';
        } else if (s.kind !== 'extra' && opts.length === 1) {
          sizeCtl = '<span class="rqd-sp-row__note">' + esc(opts[0]) + '</span>';
        }
        rows.push('<div class="rqd-sp-chip">'
          + '<span class="rqd-sp-chip__name">' + esc(s.name) + '</span>'
          + sizeCtl
          + '<button type="button" class="rqd-sp-chip__x" data-key="' + esc(k) + '" title="Remove">×</button>'
          + '</div>');
      });
      selEl.innerHTML = rows.join('');
    }

    selEl.addEventListener('click', function (e) {
      var x = e.target.closest('.rqd-sp-chip__x');
      if (x) { chosen.delete(x.dataset.key); touch(); renderList(); return; }
      var sz = e.target.closest('.rqd-sp-size__opt');
      if (sz) {
        var s = chosen.get(sz.dataset.key);
        if (s) { s.size = sz.dataset.size; touch(); }
      }
    });

    /* ============================================
       the catalog - collapsed by default, colours rendered on expand
       ============================================
       CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) - *"let's make the header expandable/
       collapsible - will help load time. Then if one is clicked on, that's when it can expand and
       load the colors under it. So the default state for each product is collapsed."*

       ⛔ THE BODY IS BUILT ON EXPAND, NOT HIDDEN WITH CSS. Hiding would still create every row
       and every <img> up front - around 500 swatch images on open - which is the load cost the
       collapse is meant to remove. A collapsed group emits a header and nothing else.

       ⚠️ SEARCH FORCES GROUPS OPEN. A query that matches colours inside collapsed groups would
       otherwise return a screen of headers with the matches invisible inside them, which reads as
       "no results" while the search is working perfectly. */
    var expanded = Object.create(null);   // gid -> true, survives re-renders within this modal

    /* ⛔ THE SWATCH COMES FROM THE STOREFRONT'S OWN MAP, NOT FROM SHOPIFY'S SWATCH FIELD.
       `settings.swatch_colors` is what the public sample page renders (see the section header in
       sections/custom-ecore-sample-picker.liquid), and it holds the ~150 photographed ECORE chips.
       Shopify's native `optionValues.swatch` is mostly EMPTY for these products and occasionally a
       flat hex that looks nothing like the material - which is what put a striped placeholder or a
       wrong solid colour on most rows.
       ⚠️ NO FALLBACK TO A COLOUR FILL. Ryan: *"a large handful of them just use solid fill
       colored swatches that aren't at all what the actual colors look like in real life so we
       don't need that."* A wrong swatch is worse than none - it is a picture of a colour nobody
       will receive. A row with no image keeps an empty box so labels stay on one vertical line. */
    function swatchHtml(colourName) {
      var map = window.RQD_SWATCH_MAP || {};
      /* The map holds FILENAMES (the section no longer runs file_img_url server-side - that was
         ~256 lookups per render, 2026-10-06). A stale filename 404s here and onerror swaps in the
         empty box, so one bad entry costs one swatch. */
      var file = map[String(colourName || '').toLowerCase().trim()];
      var url = file ? '/cdn/shop/files/' + encodeURIComponent(file) + '?width=96' : '';
      return url
        ? '<img class="rqd-sp-swatch" src="' + esc(url) + '" alt="" loading="lazy"'
          + ' onerror="this.outerHTML=\'<span class=&quot;rqd-sp-swatch rqd-sp-swatch--none&quot;></span>\'">'
        : '<span class="rqd-sp-swatch rqd-sp-swatch--none"></span>';
    }

    function selForColour(sp, c) {
      return {
        kind: 'product', id: sp.id, handle: sp.handle, name: c.label, swatch: c.value,
        sizes: sp.sizes, size: sp.defaultSize, defaultSize: sp.defaultSize,
      };
    }

    /* The colour rows for one sample. Called on expand and on re-render of an open group. */
    function bodyHtml(sp, colours) {
      return colours.map(function (c) {
        var sel = selForColour(sp, c);
        var k = keyOf(sel);
        return '<button type="button" class="rqd-sp-row' + (chosen.has(k) ? ' is-on' : '') + '"'
          + ' data-key="' + esc(k) + '" data-sel="' + esc(JSON.stringify(sel)) + '">'
          + swatchHtml(c.value)
          + '<span class="rqd-sp-row__label">' + esc(c.value || sp.name) + '</span>'
          /* ⚠️ Availability is shown, never used to hide the row: ECORE cuts samples from
             stock we do not track, so "out of stock" here is context, not a refusal. */
          + (c.available === false ? '<span class="rqd-sp-row__note">out of stock online</span>' : '')
          + '</button>';
      }).join('');
    }

    /* One search predicate, built from the box as it stands. Everything that filters asks for
       one rather than re-deriving `q` — the renderer and the expand handler running different
       matchers is how a group opens on a different set from the one its header counted. */
    function matcher() {
      var q = String(searchEl.value || '').trim().toLowerCase();
      return function (t) { return !q || String(t).toLowerCase().indexOf(q) !== -1; };
    }

    /* The colours of one sample that survive the current search. */
    function coloursFor(sp) {
      var hit = matcher();
      return (sp.colours || []).filter(function (c) { return hit(c.label) || hit(sp.name); });
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — what lives under Other items: the
       architectural folders, and the samples still waiting on ECORE's colour list.
       ⛔ ONE DEFINITION, for the same reason `coloursFor` is one: the group header states a count
       and the expand handler builds the body, and those two must not be able to disagree about
       which rows belong to it. */
    var OTHER_GID = '__other';
    function otherItems() {
      var hit = matcher();
      return {
        extras: (CATALOG.extras || []).filter(function (x) { return x.active !== false && hit(x.name); }),
        comingSoon: (CATALOG.samples || []).filter(function (sp) { return !sp.ready && sp.repOnly && hit(sp.name); }),
      };
    }

    /* The rows for Other items. Same shape the sample groups use, so the expand path can be one
       function for every group rather than a branch per kind. */
    function otherBodyHtml() {
      var o = otherItems();
      var rows = o.extras.map(function (x) {
        var sel = { kind: 'extra', id: x.id, handle: '', name: x.name, swatch: '', sizes: [], size: '', defaultSize: '' };
        var k = keyOf(sel);
        return '<button type="button" class="rqd-sp-row' + (chosen.has(k) ? ' is-on' : '') + '"'
          + ' data-key="' + esc(k) + '" data-sel="' + esc(JSON.stringify(sel)) + '">'
          + '<span class="rqd-sp-swatch rqd-sp-swatch--none"></span>'
          + '<span class="rqd-sp-row__label">' + esc(x.name) + '</span>'
          + (x.note ? '<span class="rqd-sp-row__note">' + esc(x.note) + '</span>' : '')
          + '</button>';
      });
      /* ⛔ LISTED AND INERT, still carrying the reason. An absent row reads as "we do not
         sample that" - the wrong answer, and the one that sends somebody looking. */
      rows = rows.concat(o.comingSoon.map(function (sp) {
        return '<div class="rqd-sp-row is-off" aria-disabled="true">'
          + '<span class="rqd-sp-swatch rqd-sp-swatch--none"></span>'
          + '<span class="rqd-sp-row__label">' + esc(sp.name)
          +   ' <span class="rqd-sp-tag rqd-sp-tag--soon">coming soon</span></span>'
          + '<span class="rqd-sp-row__note">' + esc(sp.reason || 'Not available yet') + '</span>'
          + '</div>';
      }));
      return rows.join('');
    }

    /* Every group's body, by id. ⛔ ONE ENTRY POINT so the initial render and the expand click
       cannot build a group differently — the bug that shape prevents is a group that looks one way
       on load and another after you close and re-open it. */
    function bodyForGid(gid) {
      if (gid === OTHER_GID) return otherBodyHtml();
      var sp = (CATALOG.samples || []).find(function (x) { return String(x.id || x.handle) === gid; });
      return sp ? bodyHtml(sp, coloursFor(sp)) : '';
    }

    function renderList() {
      if (!CATALOG) return;
      var q = String(searchEl.value || '').trim().toLowerCase();
      var hit = function (t) { return !q || String(t).toLowerCase().indexOf(q) !== -1; };
      var out = [];
      /* ⚠️ ONLY the rep-only ones reach Other items (see `otherItems`). A sample that is not
         ready because its Shopify handle stopped resolving is a MISCONFIGURATION, not a launch
         date - filing that under "coming soon" would turn a thing somebody needs to FIX into a
         thing they are waiting for. Those stay in place with their own reason. */

      (CATALOG.samples || []).forEach(function (sp) {
        var colours = coloursFor(sp);
        var nameHit = hit(sp.name);
        if (!colours.length && !nameHit) return;

        if (!sp.ready) {
          if (sp.repOnly) return;   // collected by otherItems() and rendered at the bottom
          out.push('<div class="rqd-sp-group"><div class="rqd-sp-group__title rqd-sp-group__title--plain">' + esc(sp.name) + '</div>'
            + '<div class="rqd-sp-row is-off" aria-disabled="true">'
            + '<span class="rqd-sp-swatch rqd-sp-swatch--none"></span>'
            + '<span class="rqd-sp-row__label">' + esc(sp.sizes.join(' or ') || 'unavailable') + '</span>'
            + '<span class="rqd-sp-row__note">' + esc(sp.reason || 'Not available yet') + '</span>'
            + '</div></div>');
          return;
        }

        var gid = String(sp.id || sp.handle);
        /* A search forces the group open so its matches are reachable; otherwise it remembers. */
        var isOpen = !!expanded[gid] || (!!q && colours.length > 0);
        /* ⚠️ The count is what a COLLAPSED group still says about itself. Without it the picker
           would hide the fact that this deal already has three colours of this sample on it. */
        var picked = colours.reduce(function (n, c) { return n + (chosen.has(keyOf(selForColour(sp, c))) ? 1 : 0); }, 0);

        out.push('<div class="rqd-sp-group" data-gid="' + esc(gid) + '">'
          + '<button type="button" class="rqd-sp-group__title rqd-sp-group__toggle" data-gid="' + esc(gid) + '"'
          +   ' aria-expanded="' + (isOpen ? 'true' : 'false') + '">'
          +   '<span class="rqd-sp-caret" aria-hidden="true"></span>'
          +   '<span class="rqd-sp-group__name">' + esc(sp.name) + '</span>'
          +   '<span class="rqd-sp-group__meta" data-count-for="' + esc(gid) + '">'
          +     (picked ? picked + ' selected' : esc(colours.length + (colours.length === 1 ? ' colour' : ' colours')))
          +   '</span>'
          + '</button>'
          + '<div class="rqd-sp-group__body" data-body-for="' + esc(gid) + '"' + (isOpen ? '' : ' hidden') + '>'
          +   (isOpen ? bodyForGid(gid) : '')
          + '</div></div>');
      });

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — *"the 'OTHER ITEMS' category should be
         collapsible/expandable like the rest of the categories are."* It was the one group that
         rendered as a plain heading with its rows always open, which made it the only thing on the
         screen that behaved differently from everything above it. Same chassis as a sample group
         now — same toggle, same caret, same count, same lazy body. */
      var other = otherItems();
      var otherCount = other.extras.length + other.comingSoon.length;
      if (otherCount) {
        var otherOpen = !!expanded[OTHER_GID] || !!q;
        var otherPicked = other.extras.reduce(function (n, x) {
          return n + (chosen.has(keyOf({ kind: 'extra', id: x.id, handle: '', name: x.name })) ? 1 : 0);
        }, 0);
        out.push('<div class="rqd-sp-group" data-gid="' + OTHER_GID + '">'
          + '<button type="button" class="rqd-sp-group__title rqd-sp-group__toggle" data-gid="' + OTHER_GID + '"'
          +   ' aria-expanded="' + (otherOpen ? 'true' : 'false') + '">'
          +   '<span class="rqd-sp-caret" aria-hidden="true"></span>'
          +   '<span class="rqd-sp-group__name">Other items</span>'
          +   '<span class="rqd-sp-group__meta" data-count-for="' + OTHER_GID + '">'
          +     (otherPicked ? otherPicked + ' selected' : otherCount + (otherCount === 1 ? ' item' : ' items'))
          +   '</span>'
          + '</button>'
          + '<div class="rqd-sp-group__body" data-body-for="' + OTHER_GID + '"' + (otherOpen ? '' : ' hidden') + '>'
          +   (otherOpen ? otherBodyHtml() : '')
          + '</div></div>');
      }

      listEl.innerHTML = out.length ? out.join('') : '<div class="rqd-sp-empty">Nothing matches \u201c' + esc(q) + '\u201d.</div>';
    }

    /* ---- expand / collapse ---- */
    listEl.addEventListener('click', function (e) {
      var tog = e.target.closest('.rqd-sp-group__toggle');
      if (!tog) return;
      e.preventDefault();
      var gid = tog.dataset.gid;
      var esc1 = window.CSS && CSS.escape ? CSS.escape(gid) : gid;
      var body = listEl.querySelector('.rqd-sp-group__body[data-body-for="' + esc1 + '"]');
      if (!body) return;
      var opening = body.hasAttribute('hidden');
      expanded[gid] = opening;
      tog.setAttribute('aria-expanded', opening ? 'true' : 'false');
      /* ⚠️ Collapsing EMPTIES the body rather than just hiding it. Leaving the rows in place
         would mean a long session ends up holding every image anyway, which is the cost the lazy
         build exists to avoid. */
      if (!opening) { body.setAttribute('hidden', ''); body.innerHTML = ''; return; }
      body.innerHTML = bodyForGid(gid);
      body.removeAttribute('hidden');
    });

    listEl.addEventListener('click', function (e) {
      var row = e.target.closest('.rqd-sp-row');
      if (!row) return;
      /* ⛔ A greyed row is inert. It exists to explain an absence, not to be picked. */
      if (row.classList.contains('is-off')) return;
      var k = row.dataset.key;
      if (chosen.has(k)) chosen.delete(k);
      else chosen.set(k, JSON.parse(row.dataset.sel));
      touch();
      row.classList.toggle('is-on', chosen.has(k));
      /* ⚠️ THE GROUP HEADER'S COUNT IS UPDATED IN PLACE. A full re-render here would rebuild every
         open group and lose the scroll position mid-selection; leaving the count alone would make
         a header say "2 selected" over four ticked rows, and the header is the ONLY thing a
         collapsed group shows. */
      var grp = row.closest('.rqd-sp-group');
      var meta = grp && grp.querySelector('.rqd-sp-group__meta');
      if (meta) {
        var on = grp.querySelectorAll('.rqd-sp-row.is-on').length;
        /* ⛔ COUNTS EVERY ROW, INCLUDING THE INERT COMING-SOON ONES, because the header rendered
           at build time counts them too. An earlier cut excluded `.is-off` here only — so Other
           items read "3 items" on load, and dropped to "1 item" the moment you selected a folder
           and deselected it again. Two counters describing one group have to agree; which rule
           they agree on matters less than that they do. */
        var total = grp.querySelectorAll('.rqd-sp-row').length;
        var unit = grp.dataset.gid === OTHER_GID
          ? (total === 1 ? ' item' : ' items')
          : (total === 1 ? ' colour' : ' colours');
        meta.textContent = on ? on + ' selected' : total + unit;
      }
    });

    searchEl.addEventListener('input', renderList);

    /* ---- load: the deal's current list, then the catalog ---- */
    listEl.textContent = 'Loading…';
    Promise.all([
      A.get('/sample-selection?ref=' + encodeURIComponent(ref) + (lane2 ? '&recipient=2' : '')), // CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient
      loadCatalog(false),
    ]).then(function (r) {
      var current = (r[0] && r[0].selection) || [];
      /* ⚠️ RE-JOINED TO THE SPEC ON OPEN. The deal stores the size it was saved with; the
         SIZES it may be changed to live in the catalog, and can have changed since. Reading them
         back from the spec means a sample ECORE has re-tooled shows its current options rather
         than the ones that existed when somebody last pressed Save. */
      var specByHandle = {};
      (CATALOG && CATALOG.samples || []).forEach(function (sp) { if (sp.handle) specByHandle[sp.handle] = sp; });
      current.forEach(function (s) {
        var sp = s.handle ? specByHandle[s.handle] : null;
        chosen.set(keyOf(Object.assign({}, s, { id: sp && sp.id })), {
          kind: s.kind || (s.handle ? 'product' : 'extra'),
          id: (sp && sp.id) || '',
          handle: s.handle || '',
          name: s.name,
          swatch: s.swatch || '',
          sizes: (sp && sp.sizes) || [],
          size: s.size || (sp && sp.defaultSize) || '',
          defaultSize: s.defaultSize || (sp && sp.defaultSize) || '',
        });
      });
      renderSelected();
      renderList();
      if (CATALOG && CATALOG.truncated && CATALOG.truncated.length) {
        /* ⛔ SAID OUT LOUD. A product whose colours exceeded one Shopify page would otherwise just
           show fewer options, and the reader would conclude we do not sample the rest. */
        noteEl.innerHTML = '⚠ ' + esc(CATALOG.truncated.map(function (t) {
          return t.title + ' (' + t.have + ' of ' + t.total + ' colours shown)';
        }).join('; ')) + ' — tell Claude; the catalog query needs a second page.';
      }
    }).catch(function (err) {
      /* ⛔ AN ERROR, NOT AN EMPTY PICKER. A picker that opens blank on a failed read looks exactly
         like a picker with nothing to offer, and the next move would be to conclude the catalog is
         bare rather than that the fetch failed. */
      listEl.innerHTML = '<div class="rqd-sp-empty" style="color:#dc2626;">Could not load the sample catalog: '
        + esc((err && err.message) || 'error') + '</div>';
    });

    /* ---- save ---- */
    saveBtn.addEventListener('click', function () {
      var selection = [];
      chosen.forEach(function (s) { selection.push(s); });
      saveBtn.disabled = true;
      statusEl.style.color = 'var(--rq-text-secondary)';
      statusEl.textContent = 'Saving…';

      A.post('/sample-selection', Object.assign({ ref: ref, selection: selection }, lane2 ? { recipient: 2 } : {})).then(function (res) { // CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient
        if (!res || res.error) throw new Error((res && res.error) || 'save failed');
        repaintCard(ref, res.samples || [], lane2 ? 2 : 1);
        /* ⚠️ The worker corrects a size ECORE does not cut. Saying so beats a silent swap:
           an admin who asked for 12" and got 9" should learn it here, not from the shipment. */
        if (res.corrections && res.corrections.length) {
          alert('Saved, with ' + res.corrections.length + ' size correction(s):\n\n' + res.corrections.join('\n'));
        }
        if (res.alreadyRequested) {
          /* ⚠️ Not a blocker — Ryan's flow has the rep confirm the list by phone, so a late change
             is real. But an admin must not walk away thinking ECORE has the new list. */
          alert('Saved.\n\nThe request had already been sent to ECORE, so they still have the OLD list. Press Request again to send them the updated one.');
        }
        dirty = false;
        close();
      }).catch(function (err) {
        saveBtn.disabled = false;
        statusEl.style.color = '#dc2626';
        statusEl.textContent = 'Failed: ' + ((err && err.message) || 'error');
      });
    });
  }

  /* Repaint the SAMPLES block in place from what the worker says the list now is. */
  function repaintCard(ref, samples, lane) {
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: lane 2 repaints ITS block and
       nothing of the buyer's; lane 1 (the default) excludes the lane-2 copy of every selector. */
    var _selRef = (window.CSS && CSS.escape ? CSS.escape(ref) : ref);
    if (lane === 2) {
      var box2 = document.querySelector('.rqd-lead-samples[data-ref="' + _selRef + '"][data-lane="2"]');
      if (box2) {
        box2.innerHTML = samples.length
          ? samples.map(function (s) { return esc(s); }).join('<br>')
          : '<em style="color:var(--rq-text-secondary);">None yet</em>';
        box2.setAttribute('data-count', String(samples.length));
      }
      document.querySelectorAll('.rqd-sample-adjust[data-ref="' + _selRef + '"][data-lane="2"]').forEach(function (b) {
        b.textContent = samples.length ? 'Adjust' : 'Add';
      });
      var q2 = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
      if (q2 && q2.sampleSecondary && typeof q2.sampleSecondary === 'object') {
        q2.sampleSecondary = Object.assign({}, q2.sampleSecondary, { samples: samples.slice() });
        if (typeof RQD.mirrorSampleFields === 'function') RQD.mirrorSampleFields(ref);
      }
      refreshSampleSecondGate(ref);
      return;
    }
    var box = document.querySelector('.rqd-lead-samples[data-ref="' + _selRef + '"]:not([data-lane="2"])');
    if (!box) return;
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-20) — through the ONE list renderer, so a title added
       after the request paints its "New" tag here exactly as the card would on a refetch. */
    var _qRow = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
    box.innerHTML = (_qRow && window.RQD && typeof RQD.sampleListHtml === 'function')
      ? RQD.sampleListHtml(_qRow, samples)
      : (samples.length
        ? samples.map(function (s) { return esc(s); }).join('<br>')
        : '<em style="color:var(--rq-text-secondary);">None yet</em>');
    box.setAttribute('data-count', String(samples.length));
    if (_qRow && window.RQD && typeof RQD.paintSampleRequestButtons === 'function') {
      /* The list in memory first (below), then the button reads off it. */
      setTimeout(function () { RQD.paintSampleRequestButtons(ref, _qRow); }, 0);
    }
    /* Ryan 2026-09-06: the button reads "Add" with no list and "Adjust" with one. */
    document.querySelectorAll('.rqd-sample-adjust[data-ref="' + (window.CSS && CSS.escape ? CSS.escape(ref) : ref) + '"]:not([data-lane="2"])').forEach(function (b) { // CUSTOM_SAMPLES2ND_BY_CLAUDE — the buyer's button only
      b.textContent = samples.length ? 'Adjust' : 'Add';
    });
    /* The heading says "Sample" or "Samples" and the count may have crossed 1. */
    var title = box.parentElement && box.parentElement.querySelector('.rqd-samples-title span');
    if (title) title.textContent = samples.length === 1 ? 'Sample' : 'Samples';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — the ROW in memory learns the new list too.
       Every other repaint here is DOM-only, and that was fine while nothing was derived from the
       list — but Request is now gated on it, and a detail re-render (any collapse, any other save)
       rebuilds the button from `allQuotes`. A stale row would re-disable a button the save just
       unlocked. `leadSamples` when the lead owns the list, `quoteSamples` otherwise — the same two
       fields `samplesListFor` reads. */
    var q = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
    if (q) {
      if (Array.isArray(q.leadSamples) && q.leadSamples.length) q.leadSamples = samples.slice();
      else if (Array.isArray(q.quoteSamples) && q.quoteSamples.length) q.quoteSamples = samples.slice();
      else if (q.isLead || q.leadCaptureSource === 'sample-request') q.leadSamples = samples.slice();
      else q.quoteSamples = samples.slice();
      /* `ref` may be an ORIGIN revision saved from an inheriting card (JW-1273-B editing -A's list);
         the revisions that read through it learn the new list here rather than at the refetch. */
      if (typeof RQD.mirrorSampleFields === 'function') RQD.mirrorSampleFields(ref);
    }
    refreshSampleRequestGate(ref);
  }

  /* ============================================
     THE PROJECT ROW — save on change, and the gate on Request
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06)
     ============================================
     The row's markup and the gate's RULE live in dashboard-lead-detail.js (exported on RQD) so
     both cards draw one thing. This file owns the BEHAVIOUR: a change in either input posts the
     pair to /sample-project, the status span reports it, the row in memory learns the resolved
     value, and the Request button is re-judged in place — no re-render, no re-fetch.

     ⚠️ THE GATE IS RE-JUDGED FROM THE DOM, not from the row. After an Adjust save the list count is
     the one just painted; after a project save the values are the ones in the inputs. Feeding the
     same pure `sampleRequestGate` from the screen is what lets the button unlock the moment the
     last field is filled, before anything has been re-rendered from `allQuotes`. */
  function _projectRowFor(ref) {
    return document.querySelector('.rqd-sample-project[data-ref="' + (window.CSS && CSS.escape ? CSS.escape(ref) : ref) + '"]');
  }

  function _projectValuesFrom(row) {
    if (!row) return { type: '', squareFeet: '' };
    var sel = row.querySelector('[data-sp-field="type"]');
    var oth = row.querySelector('[data-sp-field="other"]');
    var sq = row.querySelector('[data-sp-field="squareFeet"]');
    var type = sel ? String(sel.value || '').trim() : '';
    if (type === 'Other') {
      var t = oth ? String(oth.value || '').trim() : '';
      type = t ? 'Other — ' + t : 'Other';
    }
    return { type: type, squareFeet: sq ? String(sq.value || '').trim() : '' };
  }

  function refreshSampleRequestGate(ref) {
    if (!(window.RQD && typeof RQD.sampleRequestGate === 'function')) return;
    var sel = (window.CSS && CSS.escape ? CSS.escape(ref) : ref);
    var box = document.querySelector('.rqd-lead-samples[data-ref="' + sel + '"]:not([data-lane="2"])'); // CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — the buyer's list, not the second recipient's
    var row = _projectRowFor(ref);
    var q = (typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
    /* An editable row answers from its inputs; a read-only one (a rep's view) from the row in
       memory, which the save handler keeps current. */
    var qsp = (q && typeof RQD.sampleProjectFor === 'function') ? RQD.sampleProjectFor(q) : ((q && q.sampleProject) || null);
    var vals = (row && row.getAttribute('data-editable')) ? _projectValuesFrom(row)
      : (qsp ? { type: qsp.type || '', squareFeet: qsp.squareFeet || '' } : { type: '', squareFeet: '' });
    var count = box ? Number(box.getAttribute('data-count') || 0) : 0;
    /* The Shipping Address section stamps `data-has` when it renders and after every save. */
    var shipEl = document.querySelector('.rqd-sample-ship[data-ref="' + sel + '"]:not([data-lane="2"])'); // CUSTOM_SAMPLES2ND_BY_CLAUDE — the buyer's address
    var hasAddress = shipEl ? shipEl.getAttribute('data-has') === '1'
      : ((q && typeof RQD.sampleShipToFor === 'function') ? RQD.sampleShipToFor(q).source !== 'none' : undefined);
    var gate = RQD.sampleRequestGate({ samplesCount: count, type: vals.type, squareFeet: vals.squareFeet, hasAddress: hasAddress });

    document.querySelectorAll('.rqd-sample-request[data-ref="' + sel + '"]:not([data-lane="2"])').forEach(function (btn) { // CUSTOM_SAMPLES2ND_BY_CLAUDE — the buyer's Request
      btn.disabled = !gate.ready;
      btn.classList.toggle('rqd-sample-request--blocked', !gate.ready);
      if (!gate.ready) btn.title = 'Not ready to send: needs ' + gate.missing.join(', ') + '.';
      else if (btn.classList.contains('rqd-sample-request--scheduled')) btn.title = 'Scheduled for the next send window. Click again only to replace the queued email.'; // Ryan 2026-09-24
      else if (btn.classList.contains('rqd-sample-request--sent')) btn.title = 'Already requested. Click again only if they lost it or the list has changed — the composer will ask you to confirm.';
      else btn.title = 'Email ECORE’s samples desk as Abby, asking them to ship these. Opens the composer so you can read it first.';
    });
  }
  window.RQD = window.RQD || {};
  window.RQD.refreshSampleRequestGate = refreshSampleRequestGate;

  /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: its own gate on its own Request.
     A list, an address, and the buyer's request already gone (or held) — the same three the block's
     first render judges; re-judged here from the DOM after a picker or address save. */
  function refreshSampleSecondGate(ref) {
    var sel = (window.CSS && CSS.escape ? CSS.escape(ref) : ref);
    var box = document.querySelector('.rqd-lead-samples[data-ref="' + sel + '"][data-lane="2"]');
    var shipEl = document.querySelector('.rqd-sample-ship[data-ref="' + sel + '"][data-lane="2"]');
    if (!box && !shipEl) return;
    var q = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
    var count = box ? Number(box.getAttribute('data-count') || 0) : 0;
    var hasAddress = shipEl ? shipEl.getAttribute('data-has') === '1' : false;
    var req = (q && window.RQD && typeof RQD.sampleRequestFor === 'function') ? RQD.sampleRequestFor(q) : ((q && q.sampleRequest) || null);
    var primaryGone = !!(req && (req.sentAt || req.scheduledFor));
    var missing = [];
    if (!count) missing.push('a sample list (use Add)');
    if (!hasAddress) missing.push('the second recipient\'s address');
    if (!primaryGone) missing.push('the first request to have gone');
    document.querySelectorAll('.rqd-sample-request[data-ref="' + sel + '"][data-lane="2"]').forEach(function (btn) {
      if (btn.classList.contains('rqd-sample-request--sent')) return;
      btn.disabled = missing.length > 0;
      btn.classList.toggle('rqd-sample-request--blocked', missing.length > 0);
      btn.title = missing.length
        ? 'Not ready to send: needs ' + missing.join(', ') + '.'
        : 'Email ECORE’s samples desk as Abby, asking them to ship this set to the second recipient. A separate work order under this person’s name. Opens the composer so you can read it first.';
    });
  }
  window.RQD.refreshSampleSecondGate = refreshSampleSecondGate;
  /* END CUSTOM_SAMPLES2ND_BY_CLAUDE */

  var _projectSaveTimers = {};
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ REQUEST WAITS FOR A SAVE IN FLIGHT. The
     square-feet box saves on blur, 150ms later; clicking Request is what blurs it, and the click
     handler posted /sample-request-compose at once — so the email was built from the record as it
     was BEFORE the save (Ryan saw "170 sq ft" on a deal he had just set to 210). `_pendingRows`
     holds a row whose debounce has not fired, `_inFlight` the posts that have; `awaitSampleSaves`
     flushes the first and awaits the second, and the Request click calls it before composing. */
  var _pendingRows = {};
  var _inFlight = {};
  function _track(ref, promise) {
    if (!_inFlight[ref]) _inFlight[ref] = [];
    _inFlight[ref].push(promise);
    var drop = function () { var a = _inFlight[ref] || []; var i = a.indexOf(promise); if (i >= 0) a.splice(i, 1); };
    promise.then(drop, drop);
    return promise;
  }
  function awaitSampleSaves(ref) {
    if (_projectSaveTimers[ref]) {
      clearTimeout(_projectSaveTimers[ref]);
      _projectSaveTimers[ref] = null;
      var row = _pendingRows[ref];
      if (row) saveProjectRow(row);
    }
    return Promise.all((_inFlight[ref] || []).slice()).then(function () {}, function () {});
  }
  window.RQD = window.RQD || {};
  window.RQD.awaitSampleSaves = awaitSampleSaves;

  function saveProjectRow(row) {
    var A = api();
    if (!A || !row) return;
    var ref = row.getAttribute('data-ref');
    _pendingRows[ref] = null;
    var vals = _projectValuesFrom(row);
    var status = row.querySelector('.rqd-sample-project__status');
    /* "Other" with nothing typed yet is not a value to save — the text box has just appeared. */
    if (vals.type === 'Other') return;
    if (status) { status.textContent = 'Saving…'; status.style.color = ''; }
    _track(ref, A.post('/sample-project', { ref: ref, projectType: vals.type, squareFeet: vals.squareFeet }).then(function (res) {
      if (!res || res.error) throw new Error((res && res.error) || 'save failed');
      var q = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
      if (q) {
        q.sampleProject = res.sampleProject || null;
        if (typeof RQD.mirrorSampleFields === 'function') RQD.mirrorSampleFields(ref);
      }
      var sp = res.sampleProject || {};
      /* The resolved value may differ from what was typed — a cleared manual field shows the form
         answer through again — so the inputs are re-seated from the response, not left as typed. */
      var sel = row.querySelector('[data-sp-field="type"]');
      var oth = row.querySelector('[data-sp-field="other"]');
      var sq = row.querySelector('[data-sp-field="squareFeet"]');
      var t = String(sp.type || '');
      var isOther = /^Other(\s|$)/.test(t);
      if (sel && !isOther && sel.value !== t) sel.value = t;
      if (oth) { oth.hidden = !isOther; if (isOther) oth.value = t.replace(/^Other\s*(—|-)?\s*/, ''); }
      if (sq && document.activeElement !== sq) sq.value = String(sp.squareFeet || '');
      /* Saved: the span goes quiet again (Ryan 2026-09-06 — no "set by" record on the card). */
      if (status) status.textContent = '';
      refreshSampleRequestGate(ref);
    }).catch(function (err) {
      if (status) { status.textContent = 'Not saved: ' + ((err && err.message) || 'error'); status.style.color = '#dc2626'; }
    }));
  }

  document.addEventListener('change', function (e) {
    var field = e.target.closest && e.target.closest('[data-sp-field]');
    if (!field) return;
    var row = field.closest('.rqd-sample-project');
    if (!row || !row.getAttribute('data-editable')) return;
    if (field.getAttribute('data-sp-field') === 'type') {
      var oth = row.querySelector('[data-sp-field="other"]');
      if (oth) { oth.hidden = field.value !== 'Other'; if (field.value === 'Other') oth.focus(); }
    }
    /* Typing a footage fires `change` on blur, and the type select on pick; either way the pair
       goes together. A short debounce folds a select-then-type-Other sequence into one save. */
    var ref = row.getAttribute('data-ref');
    clearTimeout(_projectSaveTimers[ref]);
    _pendingRows[ref] = row;
    _projectSaveTimers[ref] = setTimeout(function () { _projectSaveTimers[ref] = null; saveProjectRow(row); }, 150);
  });
  /* CUSTOM_SAMPLEOVERNIGHT_BY_CLAUDE (Ryan 2026-10-03, JW-1300-A) — the FedEx Priority Overnight box.
     Saves at once to POST /sample-ship-speed (one recipient per box); `_track` makes Request wait for
     it, so the email the composer opens always matches the box. A failed save puts the box back. */
  document.addEventListener('change', function (e) {
    var box = e.target && e.target.closest && e.target.closest('.rqd-sample-overnight__box');
    if (!box) return;
    var A = api();
    if (!A) return;
    var ref = box.getAttribute('data-ref');
    var lane = box.getAttribute('data-overnight-lane') === '2' ? 2 : 1;
    var want = !!box.checked;
    var cell = box.closest('.rqd-sample-overnight');
    var status = cell && cell.querySelector('.rqd-sample-overnight__status');
    if (status) { status.textContent = 'Saving…'; status.style.color = ''; }
    box.disabled = true;
    _track(ref, A.post('/sample-ship-speed', lane === 2 ? { ref: ref, recipient: 2, priorityOvernight: want } : { ref: ref, priorityOvernight: want }).then(function (res) {
      if (!res || res.error) throw new Error((res && res.error) || 'save failed');
      var q = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
      if (q) {
        if (lane === 2) { if (q.sampleSecondary && typeof q.sampleSecondary === 'object') q.sampleSecondary.priorityOvernight = res.priorityOvernight || null; }
        else q.samplePriorityOvernight = res.priorityOvernight || null;
        if (typeof RQD.mirrorSampleFields === 'function') RQD.mirrorSampleFields(ref);
      }
      /* The other card on the page shows the same box: keep them in step. */
      document.querySelectorAll('.rqd-sample-overnight__box[data-overnight-lane="' + lane + '"]').forEach(function (b) {
        if (b.getAttribute('data-ref') !== ref) return;
        b.checked = want;
      });
      if (status) status.textContent = '';
    }).catch(function (err) {
      box.checked = !want;
      if (status) { status.textContent = 'Not saved: ' + ((err && err.message) || 'error'); status.style.color = '#dc2626'; }
    }).then(function () { box.disabled = false; }));
  });
  /* END CUSTOM_SAMPLEOVERNIGHT_BY_CLAUDE */

  /* ============================================
     THE SHIPPING ADDRESS SECTION — pencil, Save, Cancel, Use deal address
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06)
     ============================================
     Markup and precedence live in dashboard-lead-detail.js (`RQD.sampleShipToRowHtml` /
     `RQD.sampleShipToFor`). This owns the behaviour: the pencil swaps the grid for the inputs,
     Save posts the seven fields to /sample-ship-to, "Use deal address" posts blanks (the worker
     clears the block), and the section is REBUILT from the row in memory — which the save just
     updated — so what you see is what the resolver will hand the email. */
  /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: every helper below takes the
     lane. Lane 2 is the `.rqd-sample-ship[data-lane="2"]` block; lane 1 (the default) is the one
     WITHOUT that attribute — never "the first one in the DOM". */
  function _shipSection(ref, lane) {
    return document.querySelector('.rqd-sample-ship[data-ref="' + (window.CSS && CSS.escape ? CSS.escape(ref) : ref) + '"]' + (lane === 2 ? '[data-lane="2"]' : ':not([data-lane="2"])'));
  }
  function _laneOf(el) { return (el && el.dataset && el.dataset.lane === '2') ? 2 : 1; }
  function _rebuildShipSection(ref, lane) {
    if (lane === 2) {
      /* The whole block re-renders from the row in memory: address, chip, Request gate, Remove. */
      if (window.RQD && typeof RQD.rerenderSampleSecondary === 'function') RQD.rerenderSampleSecondary(ref);
      refreshSampleSecondGate(ref);
      return;
    }
    var q = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
    var el = _shipSection(ref);
    if (!q || !el || typeof RQD.sampleShipToRowHtml !== 'function') return;
    /* The helper renders heading + band; only the band's contents are swapped, so the heading and
       its collapse behaviour are left exactly where they are. */
    var band = el.closest('.rqd-sample-band--ship');
    var tmp = document.createElement('div');
    /* On an inheriting card the section carries the ORIGIN ref; hand the helper a copy that says so. */
    tmp.innerHTML = RQD.sampleShipToRowHtml(q.reference === ref ? q : Object.assign({}, q, { reference: ref }), { sub: !!el.closest('.rqd-detail__arap') });
    var fresh = tmp.querySelector('.rqd-sample-band--ship');
    if (band && fresh) band.replaceWith(fresh);
    refreshSampleRequestGate(ref);
  }
  function _shipEnterEdit(el, on) {
    var view = el.querySelector('.rqd-sample-ship__view');
    var row = el.querySelector('.rqd-sample-ship__row');
    if (view) view.hidden = !!on;
    if (row) row.hidden = !on;
    if (on && row) { var first = row.querySelector('[data-ship-field="street"]'); if (first) first.focus(); }
  }
  function _shipSave(ref, fields, lane) {
    var A = api(); var el = _shipSection(ref, lane); if (!A || !el) return;
    var status = el.querySelector('.rqd-sample-ship__status');
    if (status) { status.textContent = 'Saving…'; status.style.color = ''; }
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: `recipient: 2` on the same door;
       the worker answers with the whole baked block (or null after a remove), which replaces the row's. */
    var body = Object.assign({ ref: ref }, lane === 2 ? { recipient: 2 } : {}, fields);
    _track(ref, A.post('/sample-ship-to', body).then(function (res) {
      if (!res || res.error) throw new Error((res && res.error) || 'save failed');
      var q = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
      if (q) {
        if (lane === 2) q.sampleSecondary = res.sampleSecondary || null;
        else q.sampleShipTo = res.sampleShipTo || null;
        if (typeof RQD.mirrorSampleFields === 'function') RQD.mirrorSampleFields(ref);
      }
      _rebuildShipSection(ref, lane);
    }).catch(function (err) {
      if (status) { status.textContent = 'Not saved: ' + ((err && err.message) || 'error'); status.style.color = '#dc2626'; }
    }));
  }
  document.addEventListener('click', function (e) {
    var edit = e.target.closest && e.target.closest('.rqd-sample-ship__edit');
    if (edit) {
      e.preventDefault();
      var el = _shipSection(edit.dataset.ref, _laneOf(edit));
      if (el && el.getAttribute('data-editable')) _shipEnterEdit(el, true);
      return;
    }
    var cancel = e.target.closest && e.target.closest('.rqd-sample-ship__cancel');
    if (cancel) {
      e.preventDefault();
      var elc = cancel.closest('.rqd-sample-ship');
      if (!elc) return;
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE — cancelling an UNSAVED second recipient drops it (nothing was ever
         written); cancelling edits on a saved one just repaints the block. */
      if (_laneOf(elc) === 2 && elc.getAttribute('data-unsaved') === '1') {
        var qc = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(elc.getAttribute('data-ref')) : null;
        if (qc) { qc.sampleSecondary = null; if (typeof RQD.mirrorSampleFields === 'function') RQD.mirrorSampleFields(qc.reference); }
      }
      _rebuildShipSection(elc.getAttribute('data-ref'), _laneOf(elc));
      return;
    }
    var save = e.target.closest && e.target.closest('.rqd-sample-ship__save');
    if (save) {
      e.preventDefault();
      var els = save.closest('.rqd-sample-ship'); if (!els) return;
      var fields = {};
      els.querySelectorAll('[data-ship-field]').forEach(function (i) { fields[i.getAttribute('data-ship-field')] = String(i.value || '').trim(); });
      if (_laneOf(els) === 2 && !fields.name) {
        var st2 = els.querySelector('.rqd-sample-ship__status');
        if (st2) { st2.textContent = 'A contact name is required for the second recipient.'; st2.style.color = '#dc2626'; }
        var nm = els.querySelector('[data-ship-field="name"]'); if (nm) nm.focus();
        return;
      }
      _shipSave(save.dataset.ref, fields, _laneOf(els));
      return;
    }
    var reset = e.target.closest && e.target.closest('.rqd-sample-ship__reset');
    if (reset) { e.preventDefault(); _shipSave(reset.dataset.ref, { name: '', company: '', street: '', street2: '', city: '', state: '', zip: '', email: '' }); return; }
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — "+ Second recipient": an UNSAVED block appears with
       the address row open and the buyer's list copied in for display; Save writes it (the worker copies
       the list for real), Cancel drops it. */
    var add = e.target.closest && e.target.closest('.rqd-sample-second__add-btn');
    if (add) {
      e.preventDefault();
      var refA = add.dataset.ref;
      var qa = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(refA) : null;
      if (!qa) return;
      var copy = (window.RQD && typeof RQD.samplesListFor === 'function') ? RQD.samplesListFor(qa) : [];
      qa.sampleSecondary = { _unsaved: true, shipTo: { name: '', company: '', street: '', street2: '', city: '', state: '', zip: '', email: '' },
        samples: (Array.isArray(copy) ? copy : []).slice(), sampleItems: [], sampleRequest: null, sampleTracking: null, sampleShipments: [], requested: false, status: 'draft' };
      if (typeof RQD.rerenderSampleSecondary === 'function') RQD.rerenderSampleSecondary(refA);
      var el2 = _shipSection(refA, 2);
      var first = el2 && el2.querySelector('[data-ship-field="name"]');
      if (first) first.focus();
      return;
    }
    var rm = e.target.closest && e.target.closest('.rqd-sample-second__remove');
    if (rm) {
      e.preventDefault();
      var refR = rm.dataset.ref;
      var ok = window.confirm('Drop the second recipient on ' + refR + '? Their list and address come off the deal. Nothing has been requested for them.');
      if (!ok) return;
      _shipSave(refR, { name: '', company: '', street: '', street2: '', city: '', state: '', zip: '', email: '' }, 2);
      return;
    }
  });
  /* Enter in any address input saves; Escape cancels — the tracking row's own keys. */
  document.addEventListener('keydown', function (e) {
    var field = e.target.closest && e.target.closest('[data-ship-field]');
    if (!field) return;
    var el = field.closest('.rqd-sample-ship');
    if (!el) return;
    if (e.key === 'Enter') { e.preventDefault(); var s = el.querySelector('.rqd-sample-ship__save'); if (s) s.click(); }
    if (e.key === 'Escape') { e.preventDefault(); var c = el.querySelector('.rqd-sample-ship__cancel'); if (c) c.click(); else _rebuildShipSection(el.getAttribute('data-ref'), _laneOf(el)); }
  });
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* The gate answers to keystrokes too, so the button unlocks as the last digit lands rather than
     on blur — the save itself still waits for `change`. */
  document.addEventListener('input', function (e) {
    var field = e.target.closest && e.target.closest('[data-sp-field]');
    if (!field) return;
    var row = field.closest('.rqd-sample-project');
    if (row && row.getAttribute('data-editable')) refreshSampleRequestGate(row.getAttribute('data-ref'));
  });
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.rqd-sample-adjust');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled) return;
    open(btn.dataset.ref, btn);
  });

  window.RepPortal = window.RepPortal || {};
  window.RepPortal.SamplePicker = { open: open, reloadCatalog: function () { return loadCatalog(true); } };
})();
